/**
 * Full-screen inventory overlay (M14).
 * HTML/CSS rendered into #ui-root. Toggle with Tab key.
 * Shows equipment slots, 5x3 backpack grid, item tooltips, and effective stats.
 */
export class InventoryPanel {
  /**
   * @param {HTMLElement} uiRoot - The #ui-root container element
   */
  constructor(uiRoot) {
    this.uiRoot = uiRoot;
    this.visible = false;
    this.player = null;

    // Grid navigation state
    this.selectedRow = 0;
    this.selectedCol = 0;
    this.selectedArea = 'backpack'; // 'backpack' or 'equipment'
    this.equipmentSlotIndex = 0; // 0=weapon, 1=body, 2=ring, 3=amulet

    // Callbacks
    this.onEquip = null;   // (backpackIndex, slot) => void
    this.onDrop = null;    // (backpackIndex) => void
    this.onUnequip = null; // (slot) => void

    this.overlay = null;
    this.elements = {};
    this._create();
  }

  _create() {
    // Full-screen overlay
    this.overlay = document.createElement('div');
    this.overlay.id = 'inventory-overlay';
    this.overlay.style.cssText = `
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0, 0, 0, 0.85);
      display: none;
      font-family: 'Courier New', monospace;
      color: #cccccc;
      z-index: 200;
    `;

    // Main panel (centered)
    const panel = document.createElement('div');
    panel.style.cssText = `
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      width: 420px;
      background: rgba(20, 20, 30, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.2);
      padding: 0;
    `;

    // Header
    const header = document.createElement('div');
    header.style.cssText = `
      padding: 8px 12px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 13px;
      font-weight: bold;
      color: #ffffff;
    `;
    header.innerHTML = '<span>INVENTORY</span><span style="color:#888888;font-size:10px;">[Tab/Esc]</span>';

    // Body: two columns
    const body = document.createElement('div');
    body.style.cssText = `
      display: flex;
      padding: 10px 12px;
      gap: 14px;
    `;

    // Left column: Equipment + Stats
    const leftCol = document.createElement('div');
    leftCol.style.cssText = `
      width: 130px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    `;

    // Equipment slots label
    const equipLabel = document.createElement('div');
    equipLabel.style.cssText = `font-size: 10px; color: #999999; margin-bottom: 2px;`;
    equipLabel.textContent = 'Equipment';

    leftCol.appendChild(equipLabel);

    // Equipment slot elements
    const slotNames = ['weapon', 'body', 'ring', 'amulet'];
    const slotLabels = ['Weapon', 'Body', 'Ring', 'Amulet'];
    this.equipSlotElements = [];

    for (let i = 0; i < slotNames.length; i++) {
      const slotRow = document.createElement('div');
      slotRow.style.cssText = `
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 3px 4px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        background: rgba(40, 40, 50, 0.8);
        cursor: default;
        min-height: 22px;
      `;

      const slotIcon = document.createElement('div');
      slotIcon.style.cssText = `
        width: 16px;
        height: 16px;
        background: rgba(60, 60, 70, 0.8);
        border: 1px solid rgba(255, 255, 255, 0.08);
        flex-shrink: 0;
      `;

      const slotText = document.createElement('div');
      slotText.style.cssText = `
        font-size: 9px;
        color: #888888;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      `;
      slotText.textContent = slotLabels[i];

      slotRow.appendChild(slotIcon);
      slotRow.appendChild(slotText);
      leftCol.appendChild(slotRow);

      this.equipSlotElements.push({
        row: slotRow,
        icon: slotIcon,
        text: slotText,
        slot: slotNames[i],
      });
    }

    // Stats section
    const statsLabel = document.createElement('div');
    statsLabel.style.cssText = `font-size: 10px; color: #999999; margin-top: 6px;`;
    statsLabel.textContent = 'Stats:';
    leftCol.appendChild(statsLabel);

    const statsBlock = document.createElement('div');
    statsBlock.style.cssText = `
      font-size: 9px;
      color: #aaaaaa;
      line-height: 1.6;
    `;
    this.elements.statsBlock = statsBlock;
    leftCol.appendChild(statsBlock);

    // Right column: Backpack + Tooltip
    const rightCol = document.createElement('div');
    rightCol.style.cssText = `
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 8px;
    `;

    // Backpack label
    const bpLabel = document.createElement('div');
    bpLabel.style.cssText = `font-size: 10px; color: #999999; margin-bottom: 2px;`;
    bpLabel.textContent = 'Backpack (5x3)';
    rightCol.appendChild(bpLabel);

    // 5x3 grid
    const grid = document.createElement('div');
    grid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(5, 32px);
      grid-template-rows: repeat(3, 32px);
      gap: 3px;
    `;

    this.gridCells = [];
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 5; c++) {
        const cell = document.createElement('div');
        cell.style.cssText = `
          width: 32px;
          height: 32px;
          background: rgba(50, 50, 60, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        `;

        const cellIcon = document.createElement('div');
        cellIcon.style.cssText = `
          width: 20px;
          height: 20px;
          display: none;
        `;
        cell.appendChild(cellIcon);

        const typeIndicator = document.createElement('div');
        typeIndicator.style.cssText = `
          position: absolute;
          bottom: 1px;
          right: 1px;
          font-size: 7px;
          color: #666666;
        `;
        cell.appendChild(typeIndicator);

        grid.appendChild(cell);
        this.gridCells.push({ cell, icon: cellIcon, typeInd: typeIndicator, row: r, col: c });
      }
    }

    rightCol.appendChild(grid);

    // Tooltip area
    const tooltip = document.createElement('div');
    tooltip.style.cssText = `
      background: rgba(30, 30, 40, 0.9);
      border: 1px solid rgba(255, 255, 255, 0.1);
      padding: 6px 8px;
      font-size: 9px;
      min-height: 60px;
      color: #cccccc;
      line-height: 1.5;
    `;
    tooltip.textContent = 'Select an item to view details.';
    this.elements.tooltip = tooltip;
    rightCol.appendChild(tooltip);

    // Action hint
    const actionHint = document.createElement('div');
    actionHint.style.cssText = `
      font-size: 9px;
      color: #666666;
      text-align: center;
      padding-top: 2px;
    `;
    actionHint.textContent = 'Arrow keys: navigate | E: equip | X: drop | U: unequip';
    this.elements.actionHint = actionHint;
    rightCol.appendChild(actionHint);

    body.appendChild(leftCol);
    body.appendChild(rightCol);

    panel.appendChild(header);
    panel.appendChild(body);
    this.overlay.appendChild(panel);
    this.uiRoot.appendChild(this.overlay);
  }

  /**
   * Show or hide the inventory panel.
   * @param {boolean} show
   * @param {object} player - Player instance to read data from
   */
  toggle(show, player) {
    this.visible = show;
    this.player = player;
    this.overlay.style.display = show ? 'block' : 'none';
    if (show) {
      this.refresh();
    }
  }

  /**
   * Refresh the entire inventory display from current player data.
   */
  refresh() {
    if (!this.player) return;

    // Update equipment slots
    this._refreshEquipmentSlots();

    // Update backpack grid
    this._refreshBackpackGrid();

    // Update stats
    this._refreshStats();

    // Update selection highlight
    this._updateSelectionHighlight();

    // Update tooltip for current selection
    this._updateTooltip();
  }

  _refreshEquipmentSlots() {
    const player = this.player;
    const slotNames = ['weapon', 'body', 'ring', 'amulet'];

    for (let i = 0; i < slotNames.length; i++) {
      const el = this.equipSlotElements[i];
      const equipped = player.equipment[slotNames[i]];

      if (equipped && equipped.itemData) {
        const color = equipped.itemData.color || '0x888888';
        const hexColor = '#' + parseInt(color, 16).toString(16).padStart(6, '0');
        el.icon.style.background = hexColor;
        el.icon.style.border = '1px solid rgba(255, 255, 255, 0.3)';
        el.text.textContent = equipped.itemData.name;
        el.text.style.color = '#cccccc';
      } else {
        el.icon.style.background = 'rgba(60, 60, 70, 0.8)';
        el.icon.style.border = '1px solid rgba(255, 255, 255, 0.08)';
        const labels = ['Weapon', 'Body', 'Ring', 'Amulet'];
        el.text.textContent = labels[i];
        el.text.style.color = '#555555';
      }
    }
  }

  _refreshBackpackGrid() {
    const inv = this.player.inventory;

    for (let i = 0; i < this.gridCells.length; i++) {
      const gc = this.gridCells[i];
      const item = i < inv.length ? inv[i] : null;

      if (item && item.itemData) {
        const color = item.itemData.color || '0x888888';
        const hexColor = '#' + parseInt(color, 16).toString(16).padStart(6, '0');
        gc.icon.style.display = 'block';
        gc.icon.style.background = hexColor;
        gc.icon.style.border = '1px solid rgba(255, 255, 255, 0.2)';

        // Type indicator
        const type = item.itemData.type;
        if (type === 'weapon') gc.typeInd.textContent = 'W';
        else if (type === 'armor') gc.typeInd.textContent = 'A';
        else if (type === 'accessory') gc.typeInd.textContent = 'R';
        else if (type === 'consumable') gc.typeInd.textContent = 'C';
        else gc.typeInd.textContent = '';
      } else {
        gc.icon.style.display = 'none';
        gc.icon.style.background = '';
        gc.icon.style.border = '';
        gc.typeInd.textContent = '';
      }
    }
  }

  _refreshStats() {
    const eff = this.player.getEffectiveStats();
    const hp = this.player.hp;
    const maxHp = eff.maxHp;
    const defPct = Math.round(eff.defense * 100);

    this.elements.statsBlock.innerHTML =
      `HP: <span style="color:#cc3333;">${hp}/${maxHp}</span><br>` +
      `ATK: <span style="color:#ffaa44;">${eff.damageMod.toFixed(1)}x</span><br>` +
      `DEF: <span style="color:#4488ff;">${defPct}%</span><br>` +
      `SPD: <span style="color:#44cc44;">${Math.round(eff.speed)}</span>` +
      (eff.critChance > 0 ? `<br>CRIT: <span style="color:#ff44ff;">${Math.round(eff.critChance * 100)}%</span>` : '');
  }

  _updateSelectionHighlight() {
    // Clear all highlights
    for (const gc of this.gridCells) {
      gc.cell.style.border = '1px solid rgba(255, 255, 255, 0.1)';
    }
    for (const el of this.equipSlotElements) {
      el.row.style.border = '1px solid rgba(255, 255, 255, 0.1)';
    }

    if (this.selectedArea === 'backpack') {
      const idx = this.selectedRow * 5 + this.selectedCol;
      if (idx < this.gridCells.length) {
        this.gridCells[idx].cell.style.border = '1px solid #ffcc44';
      }
    } else if (this.selectedArea === 'equipment') {
      if (this.equipmentSlotIndex < this.equipSlotElements.length) {
        this.equipSlotElements[this.equipmentSlotIndex].row.style.border = '1px solid #ffcc44';
      }
    }
  }

  _updateTooltip() {
    const tooltip = this.elements.tooltip;

    if (this.selectedArea === 'backpack') {
      const idx = this.selectedRow * 5 + this.selectedCol;
      const inv = this.player.inventory;
      const item = idx < inv.length ? inv[idx] : null;

      if (item && item.itemData) {
        tooltip.innerHTML = this._formatItemTooltip(item.itemData);
      } else {
        tooltip.textContent = 'Empty slot.';
      }
    } else if (this.selectedArea === 'equipment') {
      const slotNames = ['weapon', 'body', 'ring', 'amulet'];
      const slot = slotNames[this.equipmentSlotIndex];
      const equipped = this.player.equipment[slot];

      if (equipped && equipped.itemData) {
        tooltip.innerHTML = this._formatItemTooltip(equipped.itemData) +
          '<br><span style="color:#888888;">[U] Unequip</span>';
      } else {
        const labels = ['Weapon', 'Body Armor', 'Ring', 'Amulet'];
        tooltip.textContent = labels[this.equipmentSlotIndex] + ' slot (empty).';
      }
    }
  }

  /**
   * Format an item's stats into tooltip HTML.
   * @param {object} data - Item data
   * @returns {string} HTML string
   */
  _formatItemTooltip(data) {
    let html = `<span style="color:#ffffff;font-size:10px;font-weight:bold;">${data.name}</span><br>`;

    if (data.type === 'weapon') {
      html += `Damage: ${data.damage[0]}-${data.damage[1]}<br>`;
      html += `Speed: ${data.speed}<br>`;
      html += `Range: ${data.range}`;
    } else if (data.type === 'armor') {
      html += `Slot: ${data.slot}<br>`;
      html += `Defense: +${Math.round(data.defense * 100)}%<br>`;
      if (data.speedMod !== 0) {
        html += `Speed: ${data.speedMod > 0 ? '+' : ''}${data.speedMod}`;
      }
    } else if (data.type === 'accessory') {
      html += `Slot: ${data.slot}<br>`;
      if (data.hpBonus) html += `HP: +${data.hpBonus}<br>`;
      if (data.damageMod) html += `Damage: +${Math.round(data.damageMod * 100)}%<br>`;
      if (data.defense) html += `Defense: +${Math.round(data.defense * 100)}%<br>`;
      if (data.critChance) html += `Crit: +${Math.round(data.critChance * 100)}%`;
    } else if (data.type === 'consumable') {
      if (data.healAmount) html += `Heals: ${data.healAmount} HP`;
    }

    return html;
  }

  /**
   * Handle keyboard input for inventory navigation.
   * @param {object} input - InputManager instance
   * @returns {object|null} Action to perform: { action: 'equip'|'drop'|'unequip', index, slot }
   */
  handleInput(input) {
    if (!this.visible) return null;

    // Navigation
    if (input.isKeyJustPressed('ArrowUp')) {
      if (this.selectedArea === 'backpack') {
        if (this.selectedRow > 0) {
          this.selectedRow--;
        } else {
          // Switch to equipment area
          this.selectedArea = 'equipment';
          this.equipmentSlotIndex = Math.min(3, this.selectedCol);
        }
      } else {
        if (this.equipmentSlotIndex > 0) {
          this.equipmentSlotIndex--;
        }
      }
      this.refresh();
    }

    if (input.isKeyJustPressed('ArrowDown')) {
      if (this.selectedArea === 'equipment') {
        if (this.equipmentSlotIndex < 3) {
          this.equipmentSlotIndex++;
        } else {
          // Switch to backpack area
          this.selectedArea = 'backpack';
          this.selectedRow = 0;
          this.selectedCol = Math.min(4, this.equipmentSlotIndex);
        }
      } else {
        if (this.selectedRow < 2) {
          this.selectedRow++;
        }
      }
      this.refresh();
    }

    if (input.isKeyJustPressed('ArrowLeft')) {
      if (this.selectedArea === 'backpack') {
        if (this.selectedCol > 0) {
          this.selectedCol--;
        } else {
          // Switch to equipment area
          this.selectedArea = 'equipment';
          this.equipmentSlotIndex = Math.min(3, this.selectedRow);
        }
      }
      this.refresh();
    }

    if (input.isKeyJustPressed('ArrowRight')) {
      if (this.selectedArea === 'backpack') {
        if (this.selectedCol < 4) {
          this.selectedCol++;
        }
      } else if (this.selectedArea === 'equipment') {
        // Switch to backpack area
        this.selectedArea = 'backpack';
        this.selectedRow = Math.min(2, this.equipmentSlotIndex);
        this.selectedCol = 0;
      }
      this.refresh();
    }

    // E key: equip item from backpack
    if (input.isKeyJustPressed('KeyE') && this.selectedArea === 'backpack') {
      const idx = this.selectedRow * 5 + this.selectedCol;
      const inv = this.player.inventory;
      if (idx < inv.length && inv[idx]) {
        const item = inv[idx];
        let slot = null;

        if (item.itemData.type === 'weapon') {
          slot = 'weapon';
        } else if (item.itemData.type === 'armor') {
          slot = item.itemData.slot || 'body';
        } else if (item.itemData.type === 'accessory') {
          slot = item.itemData.slot || 'ring';
        } else if (item.itemData.type === 'consumable') {
          // Use consumable instead of equipping
          return { action: 'use', index: idx };
        }

        if (slot) {
          return { action: 'equip', index: idx, slot };
        }
      }
    }

    // X key: drop item from backpack
    if (input.isKeyJustPressed('KeyX') && this.selectedArea === 'backpack') {
      const idx = this.selectedRow * 5 + this.selectedCol;
      const inv = this.player.inventory;
      if (idx < inv.length && inv[idx]) {
        return { action: 'drop', index: idx };
      }
    }

    // U key: unequip from equipment slot
    if (input.isKeyJustPressed('KeyU') && this.selectedArea === 'equipment') {
      const slotNames = ['weapon', 'body', 'ring', 'amulet'];
      const slot = slotNames[this.equipmentSlotIndex];
      if (this.player.equipment[slot]) {
        return { action: 'unequip', slot };
      }
    }

    return null;
  }

  /**
   * Clean up DOM elements.
   */
  destroy() {
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
    this.overlay = null;
    this.elements = {};
    this.gridCells = [];
    this.equipSlotElements = [];
  }
}
